import axios from 'axios';
import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import zenscroll from 'zenscroll';
import Errors from '../classes/errors';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    handleCloseDropdownOnBlur,
    handleCloseInputDropdownOnBlur,
    normalizeModelName,
    setCaptchaEuScript,
} from '../utils';
import { isCaptchaEnabled } from '../services/captcha';
import { getFormRenderToken } from '../services/anti-bot';

Vue.component('generic-request-offer-component', {
    props: {
        allStores: {
            value: Array,
        },
        imStores: {
            value: Array,
        },
        distributors: {
            value: Array,
        },
        siteHandle: {
            value: String,
        },
        defaultModel: {
            value: String,
        },
        brandstoreResultAmount: {
            value: Number,
        },
        captchaEuPublicKey: {
            value: String,
        },
        sfComments: {
            value: String,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            formRenderToken: '',
            stores: this.defaultModel.toLowerCase().includes('im')
                ? this.imStores
                : this.allStores,
            errors: new Errors(),
            successMessage: '',
            formCompleted: false,
            submitting: false,
            selectedBrandStore: null,
            requestType: 'private',
            requestIndex: 0,
            countryHandle: '',
            color: 'white',

            model: '',

            dropdownCountry: null,

            internalCountryHandle: '',
            isNumberValid: true,

            newsletterSignUp: false,
            isPostalCodeInputVisible: true,
            isCityInputVisible: false,
            isCompanyInputVisible: false,
        };
    },

    computed: {
        orderedStores: function () {
            return _orderBy(this.stores, 'title');
        },

        isSubmitLoading: function () {
            return this.submitting ? 'button--loading' : '';
        },

        isCountryEurope: function () {
            return this.siteHandle === 'default';
        },
    },

    watch: {
        model(newValue, oldValue) {
            if (newValue.toLowerCase().includes('im')) {
                this.stores = this.imStores;
            } else {
                this.stores = this.allStores;
            }
        },

        carId() {
            if (this.carId) {
                this.fetchAvailableDates();
            }
        },

        selectedDate() {
            if (this.selectedDate) {
                this.selectDate();
            }
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        this.model = this.defaultModel;
        this.countryHandle = this.siteHandle;

        setCaptchaEuScript();

        getFormRenderToken().then((token) => {
            this.formRenderToken = token;
        });

        handleCloseDropdownOnBlur('.new-input__dropdown-container--model');
        handleCloseDropdownOnBlur('#heard-from-dropdown');
        handleCloseDropdownOnBlur('#country-dropdown');
        handleCloseDropdownOnBlur('#brandstore-dropdown');
        handleCloseInputDropdownOnBlur(
            '#store-dropdown-container',
            '#searchBrandstore',
        );

        EventBus.$on('brandstore-selected', (brandstoreValue) => {
            this.selectedBrandStore = brandstoreValue;
        });
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        handleToggleButtonActiveClass(index) {
            return this.requestIndex === index ? 'checked' : '';
        },

        handleMarketingConsentChange(e) {
            e.preventDefault();
            const isConsentChecked = e.target.checked;
            this.newsletterSignUp = isConsentChecked;
            const hiddenMarketingInput = document.querySelector('#marketing');
            if (!hiddenMarketingInput) return;
            hiddenMarketingInput.setAttribute(
                'value',
                isConsentChecked ? '1' : '0',
            );
        },

        scrollToFirstError() {
            Vue.nextTick(() => {
                let firstError = document.querySelector(
                    '.new-input__container--error',
                );
            });
        },

        scrollToTop() {
            Vue.nextTick(() => {
                zenscroll.toY(0);
            });
        },

        async onSubmit(event) {
            event.preventDefault();
            this.successMessage = '';
            const form = event.currentTarget;
            let formData = new FormData(form);
            const data = {};

            for (let [key, val] of formData.entries()) {
                Object.assign(data, { [key]: val });
            }

            Object.assign(data, { ['remarksText']: this.sfComments });

            if (this.submitting) {
                return;
            }

            this.submitting = true;

            const dataLayerData = {
                event: 'lead',
                lead_type: 'Finance Quote',
                client_type: 'B2C',
                brandstore: this.selectedBrandStore
                    ? this.selectedBrandStore.title
                    : '',
                payment_type: '',
                car: {
                    name: normalizeModelName(this.model, this.siteHandle),
                    ids: [],
                    edition: '',
                    color: '',
                },
                user_data: {
                    email_address: data.emailAddress,
                    phone_number: data.phone,
                    address: {
                        first_name: data.firstName,
                        last_name: data.lastName,
                    },
                },
            };

            if (!this.isNumberValid) {
                this.submitting = false;
                return;
            }

            try {
                if (!(await isCaptchaEnabled())) {
                    await this.handleDataSubmit(form, dataLayerData);
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(
                    form,
                    dataLayerData,
                    captchaSolution,
                );
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        async handleDataSubmit(form, dataLayerData, captchaSolution) {
            try {
                const formData = new FormData(form);
                formData.append('remarksText', this.sfComments);
                formData.append('sourceUrl', window.location.href);

                if (captchaSolution) {
                    formData.append(
                        'captchaSolution',
                        JSON.stringify(captchaSolution),
                    );
                }
                formData.append('formRenderToken', this.formRenderToken);
                const response = await axios.post('/', formData);

                const result = response.data;

                if (result.data?.errors) {
                    this.errors.clear();
                    this.errors.record(result.data.errors);

                    this.scrollToFirstError();
                    this.submitting = false;
                    return;
                }

                // Success
                this.formCompleted = true;
                this.errors.clear();
                this.isNumberValid = true;

                this.sendGTMData(dataLayerData);
                setTimeout(() => {
                    window.location = result.data.redirectUrl;
                }, 1000);
            } catch (e) {
                throw new Error(e);
            }
        },

        sendGTMData(extraDataToPush) {
            const country = this.siteHandle;

            if (checkCCM19Consent() || checkOneTrustConsent()) {
                // check if you are on production-enviroment
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                if (extraDataToPush) {
                    window.dataLayer.push(extraDataToPush);
                }

                window.dataLayer.push({
                    event: 'configuration',
                    model: normalizeModelName(this.model, this.siteHandle),
                    edition: ' ',
                    page_path: window.location.href,
                    country: `${country} - ${this.$root.languageCode}`,
                });
            }
        },

        toggleDropdown(event) {
            event.preventDefault();
            let element = event.currentTarget;

            while ((element = element.parentElement)) {
                if (element.classList.contains('dropdown')) {
                    element.classList.toggle('dropdown--active');
                }
            }
        },
    },
});
